import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { clientAPI } from "../api/client";
import { NumberOfPerPage } from "../utils/globalVariable";
import { formatDateDb, formatDateHourDb } from "../utils/tools";
import {
  customDateFormatType,
  formatDateForInput,
  formatNumber,
} from "../utils/utils";

const NUMBER_NFT_PER_PAGE = NumberOfPerPage.contract;
const queryKey = "saleContractStatement";

async function fetchSaleContractStatement(
  currentPage,
  search,
  projectId,
  creatorId,
  status,
  apartmentId,
  customerId,
  startTime,
  endTime,
  saleBlockId,
  saleAreaId,
  agentId
) {
  try {
    const options = {
      limit: NUMBER_NFT_PER_PAGE,
      page: currentPage,
    };

    if (search !== undefined) options.search = search;
    if (creatorId) options.creatorId = creatorId;
    options.status = 'APPROVED';

    if (projectId?.length) options.projectId = projectId;
    if (apartmentId?.length) options.apartmentId = apartmentId;
    if (customerId) options.customerId = customerId;
    if (startTime) options.startTime = customDateFormatType(startTime);
    if (endTime) options.endTime = customDateFormatType(endTime);
    if (saleBlockId) options.saleBlockId = saleBlockId;
    if (saleAreaId) options.saleAreaId = saleAreaId;
    if (agentId) options.agentId = agentId;

    const queryParams = new URLSearchParams(options).toString();
    let data = await clientAPI("get", `/salesContract/getSalesContractStatement?${queryParams}`);
    const totalPages = Math.ceil(data?.pagination?.total / NUMBER_NFT_PER_PAGE);
    const entries = data?.pagination?.total;
    const startEntry =
      (data?.pagination?.page - 1) * data?.pagination?.limit + 1;
    const endEntry = Math.min(
      data?.pagination?.page * data?.pagination?.limit,
      entries
    );

    const newData = data?.items.map((item, index) => {
      return {
        id: item?._id || "",
        status: item?.status || "-",
        delegationImage: item?.delegationImage || [],
        priceSheetImage: item?.priceSheetImage,
        salesContractImages: item?.salesContractImages || [],
        name: item?.locking?.projectObject?.name || "-",
        // code: item?.code || "",
        apartment: item?.locking?.apartmentObject?.code || "-",
        // nameApartment: item?.locking?.apartmentObject?.name || "-",
        customer: item?.customerObject?.name || "-",
        time: item?.time ? formatDateDb(item?.time) : "",
        priceTime: item?.priceTime ? formatDateDb(item?.priceTime) : "",
        // invoice: item?.invoice || "-",
        // paymentMethod: item?.paymentMethod?.name || "",
        creator: item?.creatorObject?.fullName || "-",
        saleBlock: item?.creator?.saleBlock?.name || "-",
        saleArea: item?.creator?.saleArea?.name || "-",
        // agentName: item?.agent?.name || "-",
        createdAt: item?.createdAt ? formatDateHourDb(item?.createdAt) : "-",
        edit: {
          id: item?._id || "",
          name: item?.name || "",
          code: item?.code || "",
          // invoice: item?.invoice || "",
          bookerId: item?.bookerId || "",
          paymentMethodId: item?.paymentMethodId || "",
          creator: item?.creatorObject?.fullName || "",
          project: item?.locking?.projectObject?.name || "",
          customer: item?.customerObject?.name || "",
          customerId: item?.customerId || "",
          delegationImage: item?.delegationImage || [],
          priceSheetImage: item?.priceSheetImage || "",
          salesContractImages: item?.salesContractImages || [],
          price: item?.price || 0,
          priceTime: item?.priceTime ? formatDateForInput(item?.priceTime) : "",
          time: item?.time ? formatDateForInput(item?.time) : "",
          createdAt: item?.createdAt ? formatDateDb(item?.createdAt) : "",
          apartment: item?.locking?.apartment?.name || "",
          images: item?.locking?.images || "",
          note: item?.locking?.note || "",
        },
        detail: {
          id: item?._id || "",
          name: item?.name || "",
          note: item?.locking?.note || "",
          status: item?.status || "",
          paymentMethod: item?.paymentMethod?.name || "",
          time: item?.time ? formatDateDb(item?.time) : "",
          delegationImage: item?.delegationImage || [],
          priceSheetImage: item?.priceSheetImage,
          salesContractImages: item?.salesContractImages || [],
          creator: item?.creatorObject?.fullName || "",
          createdAt: item?.createdAt ? formatDateDb(item?.createdAt) : "-",
          project: item?.locking?.projectObject?.name || "",
          customer: item?.customerObject?.name || "",
          price: item?.locking?.price
            ? `${formatNumber(item?.locking?.price)} VNĐ`
            : "0 VNĐ",
          bookingTime: item?.locking?.bookingTime
            ? formatDateDb(item?.locking?.bookingTime)
            : "",
          approverDate: item?.approverDate
            ? formatDateDb(item?.approverDate)
            : "",
          apartment: item?.locking?.apartmentObject?.name || "",
          codeApartment: item?.locking?.apartmentObject?.code || "-",
          apartments: item?.locking?.apartment,
          approver: item?.approver?.fullName || "",
          invoice: item?.invoice || "",
          priceTime: item?.priceTime ? formatDateDb(item?.priceTime) : "",
          images: item?.locking?.images,
          agentName: item?.agent?.name || "-",
          saleBlock: item?.creator?.saleBlock?.name || "-",
          saleArea: item?.creator?.saleArea?.name || "-",
        },
      };
    });

    return { data: newData, totalPages, entries, startEntry, endEntry };
  } catch (error) {
    console.error("Error fetching checkIn data:", error);
    return { data: [], totalPages: 0 };
  }
}

export function useSaleContractStatement(
  search,
  projectId,
  creatorId,
  status,
  apartmentId,
  customerId,
  startDate,
  endDate,
  saleBlockId,
  saleAreaId,
  agentId
) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [
      queryKey,
      currentPage,
      search,
      projectId,
      creatorId,
      status,
      apartmentId,
      customerId,
      startDate,
      endDate,
      saleBlockId,
      saleAreaId,
      agentId,
    ],
    queryFn: () =>
      fetchSaleContractStatement(
        currentPage,
        search,
        projectId,
        creatorId,
        status,
        apartmentId,
        customerId,
        startDate,
        endDate,
        saleBlockId,
        saleAreaId,
        agentId
      ),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    keepPreviousData: true,
    onError: (error) => {
      console.error("Error fetching checkIn data:", error);
    },
  });

  const nextPage = () => {
    if (currentPage < data?.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return {
    contractData: data?.data,
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    prevPage,
    nextPage,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
}
